import {
  WSButton,
  WSElement,
  WSEmptyState,
  WSPillProps,
  WSSectionToolbar,
  WSTable,
  WSTableCell
} from "@wingspanhq/fe-component-library";
import { useHistory } from "react-router-dom";

import { WSQueries } from "../../../../query/WSQuery";
import { useAllPayeeEngagementsQuery } from "../../../../query/payeeEngagements/queries/useAllPayeeEngagementsQuery";
import { PayerPayeeEngagementStatus } from "@wingspanhq/payments/dist/interfaces/payerPayeeEngagement";
import { formatNumber } from "accounting";
import { useCreatePayeeEngagementModal } from "../../../Payees/components/CreatePayeeEngagementModal/useCreateEngagementModal";
import queryString from "qs";
import { PAYEES_ALL_PATH } from "../../../Payees/utils";

export interface RecentContractorsWidgetProps {
  engagementId: string;
}

type TableItem = {
  count: number;
  statusPill: WSPillProps;
};

export const RecentContractorsWidget: React.FC<
  RecentContractorsWidgetProps
> = ({ engagementId }) => {
  const history = useHistory();

  const addEngagementModal = useCreatePayeeEngagementModal();

  const queryIneligibleEngagements = useAllPayeeEngagementsQuery({
    filter: {
      engagementId,
      status: {
        notIn: [
          PayerPayeeEngagementStatus.Inactive,
          PayerPayeeEngagementStatus.Active
        ]
      }
    },
    page: {
      size: 1
    }
  });

  const queryArchivedEngagements = useAllPayeeEngagementsQuery({
    filter: {
      engagementId,
      status: PayerPayeeEngagementStatus.Inactive
    },
    page: {
      size: 1
    }
  });

  const queryEligibleEngagements = useAllPayeeEngagementsQuery({
    filter: {
      engagementId,
      status: PayerPayeeEngagementStatus.Active
    },
    page: {
      size: 1
    }
  });

  return (
    <WSQueries
      queries={{
        queryIneligibleEngagements,
        queryEligibleEngagements,
        queryArchivedEngagements
      }}
    >
      {({
        queryEligibleEngagementsData,
        queryIneligibleEngagementsData,
        queryArchivedEngagementsData
      }) => {
        const eligible = queryEligibleEngagementsData.summary.listSize;
        const ineligible = queryIneligibleEngagementsData.summary.listSize;
        const archived = queryArchivedEngagementsData.summary.listSize;

        const all = eligible + ineligible + archived;

        const tableData: TableItem[] = [
          {
            count: eligible,
            statusPill: {
              text: "Eligible",
              theme: "success",
              icon: true
            }
          },
          {
            count: ineligible,
            statusPill: {
              text: "Ineligible",
              theme: "warning",
              icon: true
            }
          },
          {
            count: archived,
            statusPill: {
              text: "Archived",
              theme: "neutral",
              icon: "archive"
            }
          }
        ];

        if (all === 0) {
          return (
            <WSEmptyState
              type="people"
              title="No contractors yet"
              description="Once a contractor is added to this engagement type, they will appear here"
              actions={{
                buttons: [
                  {
                    label: "Add contractor",
                    kind: "Secondary",
                    onClick: () => {
                      addEngagementModal.open({
                        engagementId
                      });
                    }
                  }
                ]
              }}
              hasBorder={true}
              bgColor="white"
            />
          );
        }
        return (
          <WSElement>
            <WSSectionToolbar
              mb="S"
              title="Contractors"
              button={{
                label: "Add contractor",
                kind: "Link",
                onClick: () => {
                  addEngagementModal.open({
                    engagementId
                  });
                }
              }}
            />

            <WSTable<TableItem>
              tableData={tableData.map((item, index) => ({
                id: `${item.statusPill.text} ${index}`,
                data: item
              }))}
              // onRowClick={row => {
              //   const query = queryString.stringify({ engagementId });
              //   history.push(`${PAYEES_ALL_PATH}?${query}`);
              // }}
              columns={[
                {
                  config: {
                    gridTemplateSizeMin: "0",
                    gridTemplateSizeMax: "1fr",
                    header: {
                      text: "Contractors"
                    }
                  },
                  renderFunction: ({ data }) => {
                    return <WSTableCell text={formatNumber(data.count)} />;
                  }
                },
                {
                  config: {
                    gridTemplateSizeMin: "0",
                    gridTemplateSizeMax: "1fr",
                    header: {
                      text: "Status"
                    }
                  },
                  renderFunction: ({ data }) => {
                    return <WSTableCell pill={data.statusPill} />;
                  }
                }
              ]}
            />

            <WSButton.Link
              mt="S"
              onClick={() => {
                const query = queryString.stringify({ engagementId });
                history.push(`${PAYEES_ALL_PATH}?${query}`);
              }}
            >
              View all
            </WSButton.Link>
          </WSElement>
        );
      }}
    </WSQueries>
  );
};
